<template>
  <div>
    <LineChart
      id="recruiting-performance"
      :options="chartOptions"
      :data="chartData"
      :plugins="plugins"
      title="Open jobs overtime"
    />
  </div>
</template>

<script>
import LineChart from '@/components/insights/Chart/LineChart.vue';
import 'chartjs-adapter-date-fns';
import { useColors } from '@/composables/colors';
import { Filler } from 'chart.js';

export default {
  name: 'RecruitingPerformanceChart',

  components: { LineChart },
  props: {
    periodData: { type: Array, default: () => [] },
  },
  setup() {
    const { brandPrimary, brandSecondary } = useColors();

    return { brandPrimary, brandSecondary };
  },
  data() {
    return {
      plugins: [Filler],
    };
  },
  computed: {
    totalOpenDataset() {
      return {
        type: 'line',
        label: 'Total Open Jobs',
        data: this.periodData.map(({ total_open: totalOpen }) => totalOpen),
        fill: 'start',
        borderColor: this.brandPrimary,
        pointBorderColor: this.brandPrimary,
        pointBackgroundColor: this.brandPrimary,
        pointHoverBorderColor: this.brandPrimary,
        pointHoverBackgroundColor: this.brandPrimary,
        backgroundColor: 'rgba(71,69,213,.05)',
        yAxisID: 'y',
        borderWidth: 2,
      };
    },

    appliedDataset() {
      return {
        type: 'line',
        label: 'Applications',
        data: this.periodData.map(({ applied }) => applied),
        fill: false,
        borderColor: this.brandSecondary,
        pointBorderColor: this.brandSecondary,
        pointBackgroundColor: this.brandSecondary,
        pointHoverBorderColor: this.brandSecondary,
        pointHoverBackgroundColor: this.brandSecondary,
        borderWidth: 1,
        yAxisID: 'y1',
      };
    },

    labels() {
      return this.periodData.map(({ ext_label: label }) => label);
    },

    chartOptions() {
      const { periodData } = this;
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: this.jobAxis,
          x: {
            beginAtZero: true,
            border: {
              display: false,
            },
            grid: {
              color: 'transparent',
              lineWidth: 2,
            },
            ticks: {
              font: {
                family: "'Inter'",
                size: '11',
              },
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            external(context) {
              const { chart } = context;
              const { tooltip } = chart;

              let tooltipEl = document.getElementById('chartjs-tooltip');

              if (!tooltipEl || !tooltipEl.querySelector('.tooltip-content')) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'chartjs-tooltip';
                tooltipEl.classList.add('white');
                tooltipEl.classList.add('grey-arrow');
                tooltipEl.innerHTML = "<div class='tooltip-content'>";
                chart.canvas.parentNode.appendChild(tooltipEl);
              }

              // Hide if no tooltip
              if (tooltip.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              // Set caret Position
              tooltipEl.classList.remove('above', 'below', 'no-transform');
              if (tooltip.yAlign) {
                tooltipEl.classList.add(tooltip.yAlign);
              } else {
                tooltipEl.classList.add('no-transform');
              }

              const index = tooltip.dataPoints[0].dataIndex;

              const data = periodData[index];
              let innerHtml = '';

              innerHtml += `<div class="tooltip-line"><span class="chartjs-tooltip-point" style="background: #4745D5"></span> Total Published Jobs: <strong>${data.total_open.toLocaleString('en-US')}</strong></div>`;
              innerHtml += `<div class="tooltip-line"><span class="chartjs-tooltip-point" style="background: #4745D5"></span> Jobs Added: <strong>${data.added.toLocaleString('en-US')}</strong></div>`;
              innerHtml += `<div class="tooltip-line"><span class="chartjs-tooltip-point" style="background: #4745D5"></span> Jobs Removed: <strong>${data.removed.toLocaleString('en-US')}</strong></div>`;
              innerHtml += '</div>';
              innerHtml += `<div class="tooltip-title">${data.ext_label}</div>`;

              const tableRoot = tooltipEl.querySelector('.tooltip-content');
              tableRoot.innerHTML = innerHtml;

              const positionY = chart.canvas.offsetTop;
              const positionX = chart.canvas.offsetLeft;

              // Display, position, and set styles for font
              tooltipEl.style.opacity = 1;
              tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
              tooltipEl.style.top = `${positionY + tooltip.caretY - tooltip.height}px`;
              tooltipEl.style.marginTop = '-100px';
            },
          },
        },
      };

      return options;
    },

    chartData() {
      return {
        labels: this.labels,
        datasets: [this.totalOpenDataset],
      };
    },

    jobAxis() {
      return {
        position: 'left',
        beginAtZero: true,
        border: {
          display: false,
        },
        ticks: {
          // eslint-disable-next-line consistent-return
          callback(value) { if (value % 1 === 0) { return value; } },
          font: {
            family: "'Inter'",
          },
        },
        title: {
          display: true,
          text: 'Open Jobs',
          font: {
            family: "'Inter'",
          },
        },
      };
    },

    appliedAxis() {
      return {
        position: 'right',
        beginAtZero: true,
        ticks: {
          // eslint-disable-next-line consistent-return
          callback(value) { if (value % 1 === 0) { return value; } },
          font: {
            family: '"Inter"',
          },
        },
        title: {
          display: true,
          text: 'Applications',
          font: {
            family: "'Inter'",
          },
        },
        grid: {
          drawOnChartArea: false,
        },
      };
    },
  },
};
</script>

<style lang="less" scoped>
:deep(#chartjs-tooltip) {
  opacity: 1;
  padding: 12px !important;
  position: absolute;
  background: @grey-900;
  color: @white;
  transition: all .1s ease;
  pointer-events: none;
  transform: translate(-50%, 0);
  box-shadow: 1px 1px 4px 0 fade(@grey-900, 30%);
  border-radius: 8px;
  left: 50%;
  font-weight: 300;
  font-size: 14px;
  line-height: 18px;
  &:after {
    content: "";
    position: absolute;
    left: 50%;
    margin-left: -4px;
    bottom: -8px;
    border-style: solid;
    border-width: 4px;
    border-color: @text-darkest transparent transparent transparent;
  }
  &.white {
    color: @text-darkest;
    background: @white;
    &:after {
      border-top-color: @white;
    }
    strong {
      color: @text-darkest;
      font-weight: 500;
    }
  }
  &.grey-arrow {
    &:after {
      border-top-color: @stroke-default;
    }
  }
  p {
    margin: 0 0 12px;
  }

  .chartjs-tooltip-point {
    display: inline-block;
    vertical-align: top;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 6px 8px 0 0;
  }

  .tooltip-line {
    white-space: nowrap;
    padding-bottom: 4px;
    &:last-child {
      padding-bottom: 0;
    }
  }

  .tooltip-title {
    margin: 8px -12px -12px;
    background: @bg-canvas;
    border-top: 1px solid @grey-700;
    padding: 8px 12px;
    border-radius: 0 0 8px 8px;
    font-weight: 700;
  }
}
</style>
